/**
 * Kartans helskärm (B8). Helskärmen är ett fast lager över sidan; sidan under ska stå still och
 * kartan ska efteråt synas under markens meny och flikrad, som innan.
 */

/**
 * Låser sidans skroll medan helskärmen är öppen. Returnerar en funktion som låser upp och (om `restoreScroll`) återställer skrolläget.
 * `scrollY` ska vara skrolläget från innan helskärmen ritades: när kartan lyfts ut ur sidan krymper dokumentet
 * och webbläsaren klämmer redan `window.scrollY` (150 blev 40), så efteråt hamnade sidan fel.
 */
export function lockPageScroll(scrollY: number = window.scrollY): (restoreScroll: boolean) => void {
  const html = document.documentElement;
  const body = document.body;
  const y = scrollY;
  const prev = { html: html.style.overflow, body: body.style.overflow, overscroll: html.style.overscrollBehavior };
  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  html.style.overscrollBehavior = 'none';
  return (restoreScroll) => {
    html.style.overflow = prev.html;
    body.style.overflow = prev.body;
    html.style.overscrollBehavior = prev.overscroll;
    if (restoreScroll) scrollToWhenLaidOut(y);
  };
}

/**
 * UX6: skrolläget när helskärmen stängs. Syns kartan (minst `minVisible` px) när sidan står överst, tillbaka till
 * markens startsida (0) – annars låg rubriken bortskrollad och bara menyerna och kartan syntes, som om kartan
 * fortfarande var öppen. Ligger kartan längre ned: skrolläget från innan (`savedY`).
 * `mapTop` är kartans överkant i dokumentet (inte i fönstret).
 */
export function scrollAfterFullscreen(savedY: number, mapTop: number, viewportH: number, minVisible = 160): number {
  return mapTop + minVisible <= viewportH ? 0 : savedY;
}

/**
 * Hur många pixlar av elementets överkant som täcks av något annat (t.ex. den klistrade flikraden eller
 * toppmenyn). Provar uppifrån och ned mitt på elementet tills elementet självt träffas.
 */
export function coveredTopPx(el: HTMLElement): number {
  const r = el.getBoundingClientRect();
  const x = r.left + r.width / 2;
  let y = Math.max(r.top, 0) + 1;
  for (let i = 0; i < 12 && y < r.bottom; i++) {
    const hit = document.elementFromPoint(x, y);
    if (!hit || el.contains(hit) || hit.contains(el)) break;
    y = Math.max(y + 1, hit.getBoundingClientRect().bottom + 1);
  }
  return Math.max(0, Math.round(y - 1 - r.top));
}

/** Skrollar fram elementet om dess överkant ligger under en fast eller klistrad meny. */
export function uncoverTop(el: HTMLElement, margin = 8) {
  const covered = coveredTopPx(el);
  if (covered > 0) window.scrollBy(0, -(covered + margin));
}

/**
 * Skrollar till `y` och försöker igen några bildrutor till: när helskärmen stängs har sidan ännu inte
 * vuxit tillbaka (kartan och flikinnehållet läggs ut en bildruta senare), så första försöket kläms.
 */
export function scrollToWhenLaidOut(y: number, frames = 6) {
  window.scrollTo(0, y);
  let left = frames;
  const step = () => {
    if (Math.abs(window.scrollY - y) <= 1 || left-- <= 0) return;
    window.scrollTo(0, y);
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
